<template>
	<div class="cars-filter">
		<v-menu location="bottom left" offset="12" scroll-strategy="close" content-class="cars-filter__filter-menu" :close-on-content-click="false">
			<template #activator="{ props, isActive }">
				<ClientOnly>
					<v-btn
						v-if="isMobile_lg && !filter.isSort"
						:disabled="filter.disabled"
						v-bind="props"
						rounded
						flat
						class="cars-filter__activator"
						@click.stop.capture="isFiltersModalOpen = true">
						{{ i18nService.$tDynamic('modules.cars_module.cars.filters', filter.title) }}
						<span v-if="filter.isSort && currentSortValue">
							: {{ i18nService.$tDynamic('modules.cars_module.cars.filters', currentSortValue) }}
						</span>
						<template #append>
							<div class="cars-filter__append">
								<div v-if="mobileSelectedCount > 0" class="cars-filter__append-count-selected">
									<span>{{ mobileSelectedCount }}</span>
								</div>
								<GlobalIcon
									class="cars-filter__append-icon"
									:class="{ 'cars-filter__append-icon--active': isActive }"
									:height="14"
									name="arrow-down" />
							</div>
						</template>
					</v-btn>
					<v-btn v-else v-bind="props" :disabled="filter.disabled" rounded flat class="cars-filter__activator">
						{{ i18nService.$tDynamic('modules.cars_module.cars.filters', filter.title) }}
						<span v-if="filter.isSort && currentSortValue">
							: {{ i18nService.$tDynamic('modules.cars_module.cars.filters', currentSortValue) }}
						</span>
						<template #append>
							<div class="cars-filter__append">
								<div v-if="selectedValues.length && !filter.isSeats" class="cars-filter__append-count-selected">
									{{ selectedValues.length }}
								</div>
								<div v-if="selectedSeats && selectedSeats !== 1 && filter.isSeats" class="cars-filter__append-count-selected">1</div>
								<div
									v-if="selectedGroups?.length && !filter.isSeats && filter.filterName === EFilterNames.GROUP"
									class="cars-filter__append-count-selected">
									{{ selectedGroups.length }}
								</div>
								<div v-if="filter.filterName === EFilterNames.PRICE && selectedPriceRange" class="cars-filter__append-count-selected">
									1
								</div>
								<GlobalIcon
									class="cars-filter__append-icon"
									:class="{ 'cars-filter__append-icon--active': isActive }"
									:height="14"
									name="arrow-down" />
							</div>
						</template>
					</v-btn>
					<template #fallback>
						<v-btn :disabled="filter.disabled" rounded flat class="cars-filter__activator">
							{{ i18nService.$tDynamic('modules.cars_module.cars.filters', filter.title) }}
							<template #append>
								<div class="cars-filter__append">
									<GlobalIcon class="cars-filter__append-icon" :height="14" name="arrow-down" />
								</div>
							</template>
						</v-btn>
					</template>
				</ClientOnly>
			</template>

			<v-list v-if="filter.filterName === EFilterNames.GROUP && allGroups">
				<v-list-item v-for="(item, i) in allGroups" :key="i">
					<GlobalCheckbox v-model="selectedGroups" readonly :value="item.name" @click="urlFiltersRef.toggleArrayFilter('group', item.name)">
						{{
							typeof item.name === 'string'
								? i18nService.$tDynamic('data.group_map_translations', groupMapTranslations[item.name])
								: item.name
						}}
					</GlobalCheckbox>
				</v-list-item>
			</v-list>

			<v-list v-if="!filter.isSeats && filteredParameterValues.length">
				<v-list-item v-for="(item, i) in filteredParameterValues" :key="i">
					<GlobalCheckbox v-if="parameter" v-model="selectedValues" readonly :value="item" @click="handleParameterToggle(item)"
						>{{ typeof item === 'string' ? i18nService.$tDynamic('data.group_map_translations', parametersMapTranslations[item]) : item }}
					</GlobalCheckbox>
				</v-list-item>
			</v-list>
			<v-list v-else-if="filter.filterName === EFilterNames.PRICE">
				<v-radio-group v-model="selectedPriceRange" hide-details>
					<GlobalRadio
						v-for="(item, i) in priceRanges"
						:key="i"
						class="cars-filter__filter-menu-radio"
						readonly
						density="compact"
						color="var(--primary-color)"
						:label="item.name"
						:value="item"
						@click="handlePriceRange(item)" />
				</v-radio-group>
			</v-list>

			<v-list v-else-if="filter.isSort">
				<v-radio-group :model-value="currentSortItem" hide-details>
					<GlobalRadio
						v-for="(item, i) in filter.items"
						:key="i"
						class="cars-filter__filter-menu-radio"
						readonly
						density="compact"
						color="var(--primary-color)"
						:label="i18nService.$tDynamic('modules.cars_module.cars.filters', item.name)"
						:value="item"
						@click="urlFiltersRef.setSort(item.value as ESortValues)" />
				</v-radio-group>
			</v-list>
			<v-list v-else-if="filter.isSeats">
				<v-list-item>
					<v-slider
						v-model="seatsSliderValue"
						name="Liczba miejsc"
						class="cars-filter__filter-menu-slider"
						:ticks="seatsLabels"
						:step="1"
						hide-details
						show-ticks="always"
						tick-size="0"
						thumb-color="var(--primary-color)"
						track-fill-color="var(--primary-color)"
						track-color="var(--stroke-on-background)"
						:track-size="4"
						:min="1"
						:thumb-size="16"
						:max="5" />
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>

<script setup lang="ts">
import { EFilterNames, ESortValues, type IPriceRange } from '@/modules/carsModule/types/filters'
import type { useUrlFilters } from '@/modules/carsModule/composables/useUrlFilters'

const { isMobile_lg } = useWindowWidth()
const isFiltersModalOpen = inject<boolean>('isFiltersModalOpen')!
const urlFiltersRef = inject<ReturnType<typeof useUrlFilters>>('urlFilters')!
const carsStore = useCarsStore()
const i18nService = usei18nService()

export interface ISingleFilterProps {
	title: string
	filterName?: EFilterNames
	items?: {
		name: string
		value: string
	}[]
	disabled?: boolean
	isSort?: boolean
	isSeats?: boolean
	isAll?: boolean
}

const componentProps = defineProps<{
	filter: ISingleFilterProps
}>()

const parameter = computed(() => {
	if (componentProps.filter.filterName) {
		return carsStore.getFilterByParameterName(componentProps.filter.filterName)
	}
	return undefined
})

const filteredParameterValues = computed(() => filterPublicParameterValues(componentProps.filter.filterName, parameter.value?.values))

const allGroups = computed(() => filterPublicGroups(carsStore.getAllGroups ?? []))

// Derive selected state from URL
const selectedValues = computed<string[]>(() => {
	const s = urlFiltersRef.state.value
	if (componentProps.filter.filterName === EFilterNames.TRANSMISSION) return s.transmission
	if (componentProps.filter.filterName === EFilterNames.FUEL_TYPE) return s.fuelType
	return []
})

const selectedGroups = computed<string[]>(() => urlFiltersRef.state.value.groups)

const selectedSeats = computed<number | null>(() => urlFiltersRef.state.value.seatsMin)

const selectedPriceRange = computed<IPriceRange | undefined>(() => {
	const s = urlFiltersRef.state.value
	if (s.priceMin != null || s.priceMax != null) {
		return priceRanges.find((r) => r.min === s.priceMin && r.max === (s.priceMax ?? undefined))
	}
	return undefined
})

const currentSortValue = computed(() => urlFiltersRef.state.value.sort)

const currentSortItem = computed(() => {
	const sort = urlFiltersRef.state.value.sort
	if (!sort || !componentProps.filter.items) return undefined
	return componentProps.filter.items.find((item) => item.value === sort)
})

const activeFilterCount = computed(() => {
	const s = urlFiltersRef.state.value
	let count = s.groups.length + s.transmission.length + s.fuelType.length
	if (s.seatsMin != null) count++
	if (s.priceMin != null || s.priceMax != null) count++
	return count
})

const mobileSelectedCount = computed(() => {
	if (componentProps.filter.isAll) return activeFilterCount.value
	if (componentProps.filter.filterName === EFilterNames.GROUP) return selectedGroups.value.length
	if (componentProps.filter.filterName === EFilterNames.PRICE) return selectedPriceRange.value ? 1 : 0
	if (componentProps.filter.isSeats) return selectedSeats.value != null ? 1 : 0
	return selectedValues.value.length
})

// Seats: map slider position ↔ actual min seats value
const SEATS_SLIDER_MAP: Record<number, number | null> = { 1: null, 2: 2, 3: 4, 4: 5, 5: 7 }
const SEATS_REVERSE_MAP: Record<number, number> = { 2: 2, 4: 3, 5: 4, 7: 5 }

const seatsSliderValue = computed({
	get() {
		const min = urlFiltersRef.state.value.seatsMin
		if (min == null) return 1
		return SEATS_REVERSE_MAP[min] ?? 1
	},
	set(val: number) {
		urlFiltersRef.setSeatsMin(SEATS_SLIDER_MAP[val] ?? null)
	},
})

const seatsLabels = {
	1: 'Dow.',
	2: '2+',
	3: '4+',
	4: '5+',
	5: '7+',
}

const priceRanges: IPriceRange[] = [
	{ name: '0 zł - 100 zł', value: '0-100', min: 0, max: 10000 },
	{ name: '101 zł - 200 zł', value: '100-200', min: 10100, max: 20000 },
	{ name: '201 zł - 300 zł', value: '200-300', min: 20100, max: 30000 },
	{ name: '301 zł +', value: '300+', min: 30100 },
]

function handleParameterToggle(item: string | number) {
	if (componentProps.filter.filterName === EFilterNames.TRANSMISSION) {
		urlFiltersRef.toggleArrayFilter('transmission', String(item))
	} else if (componentProps.filter.filterName === EFilterNames.FUEL_TYPE) {
		urlFiltersRef.toggleArrayFilter('fuel_type', String(item))
	}
}

function handlePriceRange(item: IPriceRange) {
	const current = urlFiltersRef.state.value
	// Toggle off if same range is selected
	if (current.priceMin === item.min && current.priceMax === (item.max ?? null)) {
		urlFiltersRef.setPriceRange(null, null)
	} else {
		urlFiltersRef.setPriceRange(item.min, item.max ?? null)
	}
}
</script>

<style lang="scss" scoped src="./SingleFilter.scss"></style>
<style lang="scss">
// the menu is teleported to <body>, outside the scoped tree
.cars-filter__filter-menu {
	.v-list {
		display: flex;
		flex-direction: column;
		padding: 20px;
		box-shadow: none !important;
		border-radius: 16px !important;
		border: 1px solid var(--stroke-on-background);
		gap: 12px;

		.v-list-item {
			padding-block: 0;
			padding-inline: 16px;
			min-height: 100%;
		}
	}

	& &-slider {
		margin-inline: 16px;
		min-width: 224px;
		padding-top: 22px;

		.v-input__control {
			margin-bottom: 0;
		}

		.v-slider-track__ticks {
			color: var(--on-background-color);
			font-weight: 500;
		}

		.v-slider-track__tick-label {
			margin-top: -40px !important;
			transform: translateX(-50%) !important;
		}

		.v-slider-thumb {
			&:before {
				z-index: -1;
				content: '';
				width: 21px;
				height: 21px;
				background-color: var(--surface-color);
				border-radius: 50%;
				position: absolute;
				top: 50%;
				left: 50%;
				transform: translate(-50%, -50%);
			}
		}

		.v-slider-thumb__surface {
			box-shadow: none !important;
		}
	}

	&-radio {
		.v-selection-control__wrapper {
			height: 36px;
			width: 36px;
			margin-left: -6px;
		}
	}
}
</style>
